import './request-info.scss';
import {useIntl} from "react-intl";
import {Field, useFormikContext} from 'formik';
import {typeIdentification} from '../../../__mocks__/data';
import _ from 'lodash';
import {WLOCreditAPIService, AniConsultaError} from '../../../services';
import toast from "react-hot-toast";
import {useLoginContext} from '../../../hooks/useLoginContext';
import {useRequestContext} from '../../../hooks/useRequestContext';
import {ANIPersona, CygnusPersona} from '../../../context/RequestContext';
import {useEffect, useRef, useState} from 'react';
import ClockLoader from "react-spinners/ClockLoader";

interface FormKnowYouProps {
    errors?: any,
    values?: any,
}

export function FormKnowYou(props: FormKnowYouProps) {
    const {user} = useLoginContext();
    const {request, setRequest} = useRequestContext();
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const {errors, values} = props;
    const {setFieldValue, handleChange} = useFormikContext<any>();
    const searchIdRef = useRef(0);
    const intl = useIntl();
    const isVendedor = user?.idProfile === '4';

    const clearIdentityData = (clearFecha = true) => {
        setFieldValue('Pws_Nombres', '');
        setFieldValue('Pws_Apellidos1', '');
        setFieldValue('Pws_Apellidos2', '');
        if (clearFecha) {
            setFieldValue('Pws_Fec_expe', '');
        }
        setRequest({
            ...request,
            personCygnus: undefined,
            personANI: undefined,
            searchANI: false,
        });
        localStorage.removeItem('person');
        localStorage.removeItem('estadoCedula');
    };

    const isSearchCurrent = (searchId: number) => searchId === searchIdRef.current;

    const handleIdentificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        clearIdentityData(true);
        handleChange(e);
    };

    const handleSearch = async (numDoc?: string, numTipoDoc?: string) => {
        const docToSearch = numDoc || values.Pws_Identificacion;
        const tipoDocToSearch = numTipoDoc || values.Pws_Tip_Identificacion;

        if (_.isEmpty(tipoDocToSearch) && _.isEmpty(docToSearch)) {
            return;
        }

        if (String(docToSearch).length < 5) {
            toast.error('Debe indicar un Número de Identificación entre 5 y 10 caracteres', { duration: 5000 });
            return;
        }

        if (docToSearch && isNaN(docToSearch as any)) {
            toast.error('El documento de identificacion debe ser numerico', { duration: 5000 });
            return;
        }

        const searchId = ++searchIdRef.current;
        clearIdentityData(isVendedor);
        setIsLoadingInfo(true);

        try {
            const person = await WLOCreditAPIService.consultaSolicitudCygnus({
                Pws_Tip_Identificacion: tipoDocToSearch!,
                Pws_Identificacion: docToSearch!
            });

            if (!isSearchCurrent(searchId)) return;

            if (!_.isEmpty(person.payload)) {
                if (person.payload.result === "1") {
                    const pCygnus = person.payload?.data?.datos_persona?.Datos_persona[0] as CygnusPersona;
                    setFieldValue('Pws_Apellidos1', pCygnus.Pws_Apellidos1);
                    setFieldValue('Pws_Apellidos2', pCygnus.Pws_Apellidos2);
                    setFieldValue('Pws_Nombres', pCygnus.Pws_Nombres);
                    setRequest({...request, personCygnus: pCygnus, searchANI: false, errorConsulta: false});
                    localStorage.setItem('person', JSON.stringify(person.payload.data.datos_persona.Datos_persona[0]));
                    setIsLoadingInfo(false);
                } else {
                    try {
                        const personANI = await WLOCreditAPIService.consultaSolicitudANI({
                            Pws_Tip_Identificacion: tipoDocToSearch!,
                            Pws_Identificacion: docToSearch!
                        });

                        if (!isSearchCurrent(searchId)) return;

                        const pANI = personANI as ANIPersona;
                        setFieldValue('Pws_Apellidos1', pANI.primerApellido);
                        setFieldValue('Pws_Apellidos2', pANI.segundoApellido);
                        setFieldValue('Pws_Nombres', [pANI.primerNombre, pANI.segundoNombre].filter(Boolean).join(' '));
                        setFieldValue('Pws_Fec_expe', WLOCreditAPIService.fechaAniToFormIso(pANI.fechaExpedicion));
                        setRequest({...request, personANI: pANI, searchANI: true, errorConsulta: false});
                        localStorage.setItem('person', JSON.stringify(personANI));
                        setIsLoadingInfo(false);
                    } catch (e) {
                        if (!isSearchCurrent(searchId)) return;
                        clearIdentityData(isVendedor);
                        if (e instanceof AniConsultaError && e.allowManual) {
                            setRequest({...request, searchANI: true, errorConsulta: false});
                        } else {
                            setRequest({...request, errorConsulta: true});
                        }
                        setIsLoadingInfo(false);
                    }
                }
            } else {
                clearIdentityData(isVendedor);
                toast.error('En estos momentos no podemos atender su solicitud', { duration: 5000 });
                setIsLoadingInfo(false);
            }
        } catch (e) {
            if (!isSearchCurrent(searchId)) return;
            clearIdentityData(isVendedor);
            const error = e as any;
            setRequest({...request, errorConsulta: true});
            if (!_.isEmpty(error?.response?.data)) {
                toast.error('El documento de identificación no existe.', { duration: 5000 });
            } else {
                toast.error('En estos momentos no podemos atender su solicitud', { duration: 5000 });
            }
            setIsLoadingInfo(false);
        }
    };

    useEffect(() => {
        const datosPersonaString = localStorage.getItem("personaValidada");
        if (datosPersonaString) {
            const datosPersona = JSON.parse(datosPersonaString);
            const numDoc = datosPersona.numIdenVal;
            const numTipoDoc = datosPersona.tipIdenVal;
            const fechaExp = datosPersona.fechaExp;
            if (numDoc) {
                setFieldValue('Pws_Identificacion', numDoc);
            }
            if (numTipoDoc) {
                setFieldValue('Pws_Tip_Identificacion', numTipoDoc);
            }
            if (fechaExp) {
                setFieldValue('Pws_Fec_expe', fechaExp);
            }
            if (numDoc && numTipoDoc) {
                handleSearch(numDoc, numTipoDoc);
            }
        } else {
            setFieldValue('Pws_Tip_Identificacion', '13');
            if (values.Pws_Identificacion && values.Pws_Tip_Identificacion) {
                handleSearch();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVendedor]);

    return (
        <div className={'row'}>
            <ClockLoader id='spinner' color={"#4FAE32"} loading={isLoadingInfo} size={100}/>
            <div className={"title-medium-red"}>{intl.formatMessage({id: "form_credit_information_title"})}</div>
            <div className={"subtitle-medium"}>{intl.formatMessage({id: "form_credit_information_subtitle"})}</div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field as="select"
                        id={"Pws_Tip_Identificacion"}
                        name={"Pws_Tip_Identificacion"}
                        className={`${errors.Pws_Tip_Identificacion ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        disabled={!isVendedor}>
                        {typeIdentification && typeIdentification.WS_TIPOS_IDENTIFICACION_IResult.LstTiposIdent.Datos_TiposIdentificacion.map((item, key: number) => {
                            return (
                                <option key={key} value={item.s_codigo}>{item.s_descripcion}</option>
                            )
                        })}
                    </Field>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Identificacion"}
                        name={"Pws_Identificacion"}
                        disabled={!isVendedor}
                        onChange={isVendedor ? handleIdentificationChange : undefined}
                        onBlur={() => {
                            if (isVendedor && values.Pws_Identificacion?.trim() && values.Pws_Tip_Identificacion) {
                                handleSearch();
                            }
                        }}
                        className={`${errors.Pws_Identificacion ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}/>
                </div>
            </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-center flex-grow-6"}>
                    <Field id={"Pws_Nombres"}
                        name={"Pws_Nombres"}
                        className={`${errors.Pws_Nombres ? 'form-know-you-item-input-large input-error' : 'form-know-you-item-input-large'}`}
                        placeholder={"Nombres"}/>
                </div>
            </div>
                <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Apellidos1"}
                        name={"Pws_Apellidos1"}
                        className={`${errors.Pws_Apellidos1 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={'Primer Apellido'}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Apellidos2"}
                        name={"Pws_Apellidos2"}
                        className={`${errors.Pws_Apellidos2 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={'Segundo Apellido'}/>
                </div>
                </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Telefono1"}
                        name={"Pws_Telefono1"}
                        className={`${errors.Pws_Telefono1 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_phone_1"})}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"Pws_Correo"}
                        name={"Pws_Correo"}
                        className={`${errors.Pws_Correo ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_mail"})}/>
                </div>
            </div>
            <div className={'flex-container-form-know-you'}>
                <div className={"flex-container-form-know-you-item-right flex-grow-6"}>
                    <Field id={"Pws_Telefono2"}
                        name={"Pws_Telefono2"}
                        className={`${errors.Pws_Telefono2 ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                        placeholder={intl.formatMessage({id: "placeholder_phone_2"})}/>
                </div>
                <div className={"flex-container-form-know-you-item-left flex-grow-6"}>
                    <Field id={"mail_confirmation"}
                    onPaste={(e:any)=>{
                        e.preventDefault()
                        return false;
                    }} 
                    autoComplete="off"
                    name={"mail_confirmation"}
                    className={`${errors.mail_confirmation ? 'form-know-you-item-input-small input-error' : 'form-know-you-item-input-small'}`}
                    placeholder={intl.formatMessage({id: "placeholder_mail_confirmation"})}/>
                </div>
            </div>
        </div>

    )
}
